import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Check, Clock, Image, X, Users, Calendar, MessageSquare, ShieldCheck, FileText } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const CACHE = new Map();
const chargerImage = async (cle, url) => {
  if (CACHE.has(cle)) return CACHE.get(cle);
  const p = axios.get(url, { responseType: 'blob' })
    .then((r) => URL.createObjectURL(r.data))
    .catch((e) => { CACHE.delete(cle); throw e; });
  CACHE.set(cle, p);
  return p;
};

const formaterDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} à ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const GROUPES_PHOTOS = [
  { titre: 'Avant chantier', cles: ['avant_toiture', 'avant_tableau', 'avant_disjoncteur', 'avant_compteur'] },
  { titre: 'Codes-barres', cles: ['micro_onduleur', 'passerelle'] },
  { titre: 'Après chantier', cles: ['apres_toiture', 'apres_tableau', 'apres_disjoncteur', 'apres_compteur', 'linky_zero'] }
];

const LIBELLE_TYPE = {
  avant_toiture: 'Toiture', avant_tableau: 'Tableau électrique', avant_disjoncteur: 'Disjoncteur',
  avant_compteur: 'Compteur / Linky', micro_onduleur: 'Micro-onduleur', passerelle: 'Passerelle',
  apres_toiture: 'Toiture', apres_tableau: 'Tableau électrique', apres_disjoncteur: 'Disjoncteur',
  apres_compteur: 'Compteur / Linky', linky_zero: 'Linky à 0'
};

const Vignette = ({ chantierId, photo, onClick }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let vivant = true;
    chargerImage(`min:${photo.id}`, `${API}/api/photos/${chantierId}/${photo.id}?min=1`)
      .then((u) => { if (vivant) setSrc(u); }).catch(() => {});
    return () => { vivant = false; };
  }, [chantierId, photo.id]);
  const st = { width: '88px', height: '88px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #E5E7EB', display: 'block' };
  if (!src) return <div style={{ ...st, background: '#F3F4F6' }} />;
  return <img src={src} alt="" style={{ ...st, cursor: 'pointer' }} onClick={onClick} />;
};

const PleinEcran = ({ chantierId, photo, onFermer, auteur }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let vivant = true;
    chargerImage(`full:${photo.id}`, `${API}/api/photos/${chantierId}/${photo.id}`)
      .then((u) => { if (vivant) setSrc(u); }).catch(() => {});
    return () => { vivant = false; };
  }, [chantierId, photo.id]);
  return (
    <div onClick={onFermer} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 100, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', color: '#fff' }}>
        <div style={{ fontSize: '13px', opacity: 0.85 }}>
          {LIBELLE_TYPE[photo.type] || photo.type} · {formaterDate(photo.date)}{auteur ? ` · ${auteur}` : ''}
        </div>
        <X size={26} color="#fff" style={{ cursor: 'pointer' }} onClick={onFermer} />
      </div>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px' }}>
        {src ? <img src={src} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
             : <span style={{ color: '#9CA3AF' }}>Chargement…</span>}
      </div>
    </div>
  );
};

const ouvrirPdf = async (chantierId, docId) => {
  try {
    const r = await axios.get(`${API}/api/documents/${chantierId}/fichier/${docId}`, { responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    alert('Impossible d\'ouvrir le document');
  }
};

const FicheChantier = ({ prospectId, role }) => {
  const voitLesNoms = role === 'admin' || role === 'technicien';
  const [chantier, setChantier] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [grande, setGrande] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/chantiers/par-prospect/${prospectId}`)
      .then((r) => setChantier(r.data.data))
      .catch(() => setChantier(null))
      .finally(() => setChargement(false));
  }, [prospectId]);

  if (chargement) return <div style={{ padding: '32px', textAlign: 'center', color: '#9CA3AF' }}>Chargement…</div>;

  if (!chantier) return (
    <div style={{ padding: '32px', textAlign: 'center', color: '#6B7280' }}>
      Aucun chantier programmé pour ce client
    </div>
  );

  const ordre = chantier.etapes_ordre || [];
  const libelles = chantier.etapes_libelles || {};
  const idx = ordre.indexOf(chantier.etape);
  const journal = chantier.journal || [];
  const photos = chantier.photos || [];
  const carte = { background: '#fff', borderRadius: '12px', padding: '18px', border: '1px solid #E5E7EB', marginBottom: '16px' };

  return (
    <div style={{ padding: '4px' }}>
      <div style={carte}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '14px' }}>
          <span style={{ fontSize: '16px', fontWeight: 600 }}>{chantier.etape_libelle}</span>
          <span style={{ fontSize: '14px', color: '#10B981', fontWeight: 500 }}>{chantier.progression}%</span>
        </div>
        <div style={{ height: '8px', background: '#F3F4F6', borderRadius: '999px', overflow: 'hidden', marginBottom: '16px' }}>
          <div style={{ height: '100%', width: `${chantier.progression}%`, background: '#10B981' }} />
        </div>
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', fontSize: '14px', color: '#6B7280' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Calendar size={15} /> {chantier.date_prevue || '—'}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Users size={15} /> {chantier.equipe?.nom || 'Sans équipe'}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Image size={15} /> {photos.length} photo{photos.length > 1 ? 's' : ''}</span>
        </div>
        {chantier.commentaire && (
          <div style={{ marginTop: '14px', padding: '12px', background: '#FFFBEB', borderRadius: '8px', fontSize: '14px', color: '#92400E', display: 'flex', gap: '8px' }}>
            <MessageSquare size={15} style={{ flexShrink: 0, marginTop: '2px' }} /> {chantier.commentaire}
          </div>
        )}
      </div>

      <div style={carte}>
        <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '14px' }}>Déroulé du chantier</div>
        {ordre.map((cle, i) => {
          const faite = i <= idx;
          const trace = [...journal].reverse().find((j) => j.etape === cle);
          return (
            <div key={cle} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '12px' }}>
              <span style={{ width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: faite ? '#10B981' : '#F3F4F6', color: faite ? '#fff' : '#9CA3AF', fontSize: '12px' }}>
                {faite ? <Check size={13} /> : i + 1}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '14px', color: faite ? '#111827' : '#9CA3AF', fontWeight: faite ? 500 : 400 }}>
                  {libelles[cle] || cle}
                </div>
                {trace && (
                  <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Clock size={11} /> {formaterDate(trace.date)}{voitLesNoms ? (trace.par ? ` · ${trace.par}` : '') : (chantier.equipe?.nom ? ` · ${chantier.equipe.nom}` : '')}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {(chantier.documents || []).length > 0 && (
        <div style={carte}>
          <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '14px' }}>Documents signés</div>
          {(chantier.documents || []).map((doc, i) => (
            <div key={doc.id} style={{ paddingTop: i > 0 ? '14px' : 0, marginTop: i > 0 ? '14px' : 0, borderTop: i > 0 ? '1px solid #F3F4F6' : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '7px' }}>
                <ShieldCheck size={17} color="#10B981" />
                <span style={{ fontSize: '14px', fontWeight: 500 }}>{doc.titre}</span>
              </div>
              <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '3px' }}>
                Signé par {doc.signe_par}
              </div>
              <div style={{ fontSize: '11.5px', color: '#9CA3AF', marginBottom: '3px' }}>
                {doc.horodatage ? new Date(doc.horodatage).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                {doc.technicien ? ` · ${doc.technicien}` : ''}
              </div>
              <div style={{ fontSize: '10.5px', color: '#D1D5DB', fontFamily: 'monospace', marginBottom: '11px' }}>
                {doc.empreinte_sha256 ? doc.empreinte_sha256.slice(0, 24) + '…' : ''}
              </div>
              <button onClick={() => ouvrirPdf(chantier.id, doc.id)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 14px', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '13px', color: '#374151', background: '#fff', cursor: 'pointer' }}>
                <FileText size={14} /> Ouvrir le PDF
              </button>
            </div>
          ))}
        </div>
      )}

      {GROUPES_PHOTOS.filter((g) => voitLesNoms || g.titre !== 'Codes-barres').map((g) => {
        const dedans = photos.filter((p) => g.cles.includes(p.type));
        if (dedans.length === 0) return null;
        return (
          <div key={g.titre} style={carte}>
            <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '14px' }}>{g.titre} · {dedans.length}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px' }}>
              {dedans.map((p) => (
                <div key={p.id} style={{ width: '88px' }}>
                  <Vignette chantierId={chantier.id} photo={p} onClick={() => setGrande(p)} />
                  <div style={{ fontSize: '10px', color: '#6B7280', textAlign: 'center', marginTop: '5px', lineHeight: 1.35 }}>
                    <div style={{ color: '#374151' }}>{LIBELLE_TYPE[p.type] || p.type}</div>
                    <div>{formaterDate(p.date)}</div>
                    <div style={{ color: '#9CA3AF' }}>{voitLesNoms ? p.par : (chantier.equipe?.nom || '')}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {photos.length === 0 && (
        <div style={{ ...carte, textAlign: 'center', color: '#9CA3AF', padding: '32px' }}>
          Aucune photo pour l'instant
        </div>
      )}

      {grande && <PleinEcran chantierId={chantier.id} photo={grande} auteur={voitLesNoms ? grande.par : chantier.equipe?.nom} onFermer={() => setGrande(null)} />}
    </div>
  );
};

export default FicheChantier;
